import {Controller} from "stimulus"

/**
 * Step Navigator Controller
 * 
 * This controller handles the interaction between steps and content
 * in the step navigator component, including loading HTML content via AJAX.
 */
export default class extends Controller {
  static targets = ['contentArea', 'step', 'progressText', 'progressBar', 'mobileTitle'];
  static values = {
    activeStep: String
  };
  
  connect() {
    console.log('Step Navigator controller connected');
    
    // Create global function to call goToNextStep
    window.BanzaiStepNavigator_goToNextStep = () => {
      this.goToNextStep();
    };

    window.BanzaiStepNavigator_finish = () => {
      window.location.href = '/wellness/rewards';
    };

    // Create global function to mark all steps as completed (visual only)
    window.BanzaiStepNavigator_completeAll = () => {
      this.markAllStepsCompleted();
    };
    
    // Initialize - check localStorage for completed intro steps
    setTimeout(() => {
      this.checkForCompletedIntroSteps();
      this.updateProgress();
      this.updateStepCheckmarks();
      this.navigateToAppropriateStep();
    }, 100);
    
    // Handle window resize to ensure proper mobile behavior
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }
  
  disconnect() {
    // Clean up event listeners
    window.removeEventListener('resize', this.handleResize);
  }
  
  /**
   * Handle window resize events
   */
  handleResize() {
    // If we switch from mobile to desktop while in content view, hide mobile content view
    if (!this.isMobile() && this.element.classList.contains('mobile-content-active')) {
      this.hideMobileContentView();
    }
  }
  
  /**
   * Check if we're on mobile based on screen width
   */
  isMobile() {
    return window.innerWidth <= 768;
  }
  
  /**
   * Show mobile content view
   */
  showMobileContentView(stepTitle) {
    if (this.isMobile()) {
      this.element.classList.add('mobile-content-active');
      
      // Update mobile title
      if (this.hasMobileTitleTarget) {
        this.mobileTitleTarget.textContent = stepTitle || '';
      }
    }
  }
  
  /**
   * Hide mobile content view and return to steps
   */
  hideMobileContentView() {
    if (this.isMobile()) {
      this.element.classList.remove('mobile-content-active');
    }
  }
  
  /**
   * Go back to steps view on mobile
   */
  goBackToSteps(event) {
    event.preventDefault();
    this.hideMobileContentView();
  }
  
  /**
   * Check for completed intro steps in localStorage and update UI accordingly
   */
  checkForCompletedIntroSteps() {
    // Get all intro steps
    const introSteps = Array.from(this.element.querySelectorAll('.step-item'))
      .filter(step => step.querySelector('.step-link')?.dataset.resourceType === 'intro');
      
    introSteps.forEach(introStep => {
      const rewardId = introStep.querySelector('.step-link')?.dataset.rewardId;
      const storageKey = `banzai_intro_completed_${rewardId}`;
      
      if (rewardId && localStorage.getItem(storageKey) === 'true') {
        introStep.classList.remove('step-current', 'step-pending');
        introStep.classList.add('step-completed');
      }
    });
  }
  
  /**
   * Update step checkmarks to show SVG checkmarks for completed steps
   */
  updateStepCheckmarks() {
    const completedSteps = this.element.querySelectorAll('.step-item.step-completed');
    completedSteps.forEach(step => {
      const stepIndicator = step.querySelector('.step-indicator');
      if (stepIndicator) {
        stepIndicator.innerHTML = `
          <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 30C23.2875 30 30 23.2875 30 15C30 6.7125 23.2875 0 15 0C6.7125 0 0 6.7125 0 15C0 23.2875 6.7125 30 15 30ZM3 15C3 8.4 8.4 3 15 3C21.6 3 27 8.4 27 15C27 21.6 21.6 27 15 27C8.4 27 3 21.6 3 15ZM7.95 16.8C7.35 16.2375 7.35 15.2625 7.95 14.7C8.5125 14.1 9.4875 14.1 10.05 14.7L12.75 17.3625L19.95 10.2C20.5125 9.6 21.4875 9.6 22.05 10.2C22.65 10.7625 22.65 11.7375 22.05 12.3L13.8 20.55C13.5 20.85 13.125 21 12.75 21C12.375 21 12 20.85 11.7 20.55L7.95 16.8Z" fill="#00A53B"/>
          </svg>
        `;
      }
    });
  }
  
  /**
   * Navigate to the appropriate step - either the first uncompleted step or intro if it's the first time
   */
  navigateToAppropriateStep() {
    // Check if this is a fresh start (no actual reward content completed)
    // Only count non-intro and non-finish steps for progress calculation
    const allSteps = this.element.querySelectorAll('.step-item');
    const actualRewardSteps = Array.from(allSteps).filter(step => {
      const stepLink = step.querySelector('.step-link');
      const resourceType = stepLink?.dataset.resourceType;
      return resourceType && resourceType !== 'intro' && resourceType !== 'finish';
    });
    
    const completedRewardSteps = actualRewardSteps.filter(step => 
      step.classList.contains('step-completed')
    ).length;
    
    // If no actual reward content has been completed, always start with the intro step
    if (completedRewardSteps === 0) {
      const introStep = this.element.querySelector('.step-link[data-resource-type="intro"]');
      if (introStep) {
        this.updateActiveStep(introStep);
        this.loadContentForUrl(null);
        return;
      }
    }
    
    // Find the first uncompleted step (that's not disabled)
    const firstUncompletedStep = this.findFirstUncompletedStep();
    
    if (firstUncompletedStep) {
      this.updateActiveStep(firstUncompletedStep);
      const url = firstUncompletedStep.dataset.stepUrl;
      const resourceType = firstUncompletedStep.dataset.resourceType;
      
      // On mobile, don't show content initially - just show steps
      if (!this.isMobile()) {
        if (resourceType === 'intro') {
          this.loadContentForUrl(null);
        } else if (resourceType === 'finish') {
          this.showFinishStepContent();
        } else if (url) {
          this.loadContentForUrl(url);
        }
      }
    } else {
      // All steps completed, navigate to finish step
      this.navigateToFinishStep();
    }
  }
  
  /**
   * Find the first uncompleted step that's not disabled
   */
  findFirstUncompletedStep() {
    const stepLinks = Array.from(this.element.querySelectorAll('.step-link'));
    return stepLinks.find(link => {
      const stepItem = link.closest('.step-item');
      return !stepItem.classList.contains('step-completed') && 
             !stepItem.classList.contains('step-disabled');
    });
  }
  
  /**
   * Navigate to the finish step
   */
  navigateToFinishStep() {
    const finishStepLink = this.element.querySelector('.step-link[data-resource-type="finish"]');
    if (finishStepLink) {
      const stepTitle = finishStepLink.closest('.step-item')?.querySelector('.step-title')?.textContent || '';
      this.updateActiveStep(finishStepLink);
      
      // Show mobile content view if on mobile
      this.showMobileContentView(stepTitle);
      
      this.showFinishStepContent();
    }
  }
  
  /**
   * Show the finish step content
   */
  showFinishStepContent() {
    const contentArea = this.hasContentAreaTarget 
      ? this.contentAreaTarget 
      : this.element.querySelector('.step-navigator-content');
      
    const finishStepContent = contentArea?.querySelector('[data-finish-step-content="true"]');
    
    if (finishStepContent) {
      // Hide any iframe content
      const iframes = contentArea.querySelectorAll('iframe');
      iframes.forEach(iframe => iframe.style.display = 'none');
      
      // Hide intro content
      const introContent = contentArea.querySelector('[data-intro-content="true"]');
      if (introContent) {
        introContent.style.display = 'none';
      }
      
      // Show finish step content
      finishStepContent.style.display = 'block';
      
      // Hide loading state
      this.hideLoading();
      
      // Mark finish step as completed
      const finishStepItem = this.element.querySelector('.step-link[data-resource-type="finish"]')?.closest('.step-item');
      if (finishStepItem && !finishStepItem.classList.contains('step-completed')) {
        finishStepItem.classList.add('step-completed');
        finishStepItem.classList.remove('step-current', 'step-pending');
        this.updateStepCheckmarks();
        this.updateProgress();
      }
    }
  }
  
  /**
   * Update the active step UI
   */
  updateActiveStep(activeLink) {
    // Remove current step status from all steps
    const stepItems = this.element.querySelectorAll('.step-item');
    stepItems.forEach(item => {
      item.classList.remove('step-current');
      if (!item.classList.contains('step-completed')) {
        item.classList.add('step-pending');
      }
    });
    
    // Add current step status to the active step
    const activeItem = activeLink.closest('.step-item');
    if (activeItem) {
      activeItem.classList.remove('step-pending');
      activeItem.classList.add('step-current');
    }
    
    // Handle content visibility based on step type
    const contentArea = this.hasContentAreaTarget 
      ? this.contentAreaTarget 
      : this.element.querySelector('.step-navigator-content');
      
    const introContent = contentArea?.querySelector('[data-intro-content="true"]');
    const finishContent = contentArea?.querySelector('[data-finish-step-content="true"]');
    const isCurrentStepIntro = activeLink && activeLink.dataset.resourceType === 'intro';
    const isCurrentStepFinish = activeLink && activeLink.dataset.resourceType === 'finish';
    
    if (introContent) {
      introContent.style.display = isCurrentStepIntro ? 'block' : 'none';
    }
    
    if (finishContent) {
      finishContent.style.display = isCurrentStepFinish ? 'block' : 'none';
    }
    
    // Update progress and checkmarks
    this.updateProgress();
    this.updateStepCheckmarks();
  }
  
  /**
   * Update the progress indicator
   */
  updateProgress() {
    // Check if targets exist in the DOM
    const progressTextElement = this.element.querySelector('[data-step-navigator-target="progressText"]');
    const progressBarElement = this.element.querySelector('[data-step-navigator-target="progressBar"]');
    
    if (!progressTextElement || !progressBarElement) {
      console.warn('Progress elements not found in DOM');
      return;
    }
    
    const stepItems = this.element.querySelectorAll('.step-item');
    const totalSteps = stepItems.length;
    const completedSteps = this.element.querySelectorAll('.step-item.step-completed').length;
    
    // Calculate progress percentage
    const progressPercentage = totalSteps > 0 ? Math.floor((completedSteps / totalSteps) * 100) : 0;
    
    // Update progress text
    progressTextElement.textContent = `${progressPercentage}% COMPLETED`;
    
    // Update progress bar
    progressBarElement.style.width = `${progressPercentage}%`;
  }
  
  /**
   * Load content from a URL via an iframe and inject it into the content area
   */
  loadContentForUrl(url) {
    // Check if this is an intro step (url is null/empty for intro steps)
    const currentStepLink = this.getCurrentStepLink();
    const isIntroStep = currentStepLink && currentStepLink.dataset.resourceType === 'intro';
    
    // For intro steps, check if intro content is already rendered
    if (isIntroStep) {
      const contentArea = this.hasContentAreaTarget 
        ? this.contentAreaTarget 
        : this.element.querySelector('.step-navigator-content');
        
      const introContent = contentArea?.querySelector('[data-intro-content="true"]');
      
      if (introContent) {
        // Intro content is already rendered, just make sure it's visible
        this.hideLoading();
        this.element.dispatchEvent(new CustomEvent('step-navigator:content-loaded', {
          detail: { url: 'intro-content' }
        }));
        return;
      }
    }
    
    if (!url) return;
    
    // Show loading state
    this.showLoading();
    
    // Get the content area - look for contentArea target or fallback to .step-navigator-content
    const contentArea = this.hasContentAreaTarget 
      ? this.contentAreaTarget 
      : this.element.querySelector('.step-navigator-content');
      
    if (!contentArea) {
      console.error('Could not find content area to inject content');
      this.hideLoading();
      return;
    }
    
    // Clear any existing content except intro content and mobile header
    const introContent = contentArea.querySelector('[data-intro-content="true"]');
    const mobileHeader = contentArea.querySelector('.step-navigator-mobile-header');
    const finishContent = contentArea.querySelector('[data-finish-step-content="true"]');
    
    // Preserve intro content, mobile header, and finish content when clearing
    const introContentHTML = introContent ? introContent.outerHTML : '';
    const mobileHeaderHTML = mobileHeader ? mobileHeader.outerHTML : '';
    const finishContentHTML = finishContent ? finishContent.outerHTML : '';
    
    contentArea.innerHTML = mobileHeaderHTML + introContentHTML + finishContentHTML;
    
    // Create an iframe element
    const iframe = document.createElement('iframe');
    iframe.src = url;
    iframe.className = 'step-navigator-iframe';
    iframe.style.width = '100%';
    iframe.style.border = 'none';
        
    // Get the current step to check resource type
    const resourceType = currentStepLink?.dataset.resourceType;
    
    iframe.onload = () => {
        this.hideLoading();
      
      // For course type resources, inject custom CSS
      if (resourceType === 'course') {
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
          
          // Create and inject the course-specific CSS
          const styleElement = iframeDoc.createElement('style');
          styleElement.textContent = `
            .game {
              margin-top: 0px;
            }
            
            nav {
              display: none;
            }
          `;
          iframeDoc.head.appendChild(styleElement);
        } catch (error) {
          console.error('Error injecting CSS into course iframe:', error);
        }
      }
        
        // Dispatch event that content was loaded
        this.element.dispatchEvent(new CustomEvent('step-navigator:content-loaded', {
          detail: { url }
        }));
    };
    
    // Handle load errors
    iframe.onerror = () => {
      console.error('Error loading iframe content:', url);
        contentArea.innerHTML = `<div class="step-navigator-error-message">
          <p>Error loading content</p>
        <button onclick="this.closest('.step-navigator-container').querySelector('.step-link[data-step-url=\'${url}\']').click()">
            Retry
          </button>
        </div>`;
        this.handleError();
    };
    
    // Add the iframe to the content area
    contentArea.appendChild(iframe);
  }
  
  /**
   * Get the currently active step link
   */
  getCurrentStepLink() {
    const currentStep = this.element.querySelector('.step-item.step-current');
    return currentStep ? currentStep.querySelector('.step-link') : null;
  }
  
  /**
   * Check if a step is the last one in the navigator
   */
  isLastStep(stepLink) {
    const stepItem = stepLink.closest('.step-item');
    const allStepItems = Array.from(this.element.querySelectorAll('.step-item'));
    return stepItem === allStepItems[allStepItems.length - 1];
  }
  
  /**
   * Get the next step link after the given step link
   */
  getNextStepLink(stepLink) {
    const stepItem = stepLink.closest('.step-item');
    const nextStepItem = stepItem.nextElementSibling;
    return nextStepItem ? nextStepItem.querySelector('.step-link') : null;
  }
  
  /**
   * Check if a step is an intro step
   */
  isIntroStep(stepLink) {
    return stepLink && stepLink.dataset.resourceType === 'intro';
  }
  
  /**
   * Mark an intro step as completed in localStorage
   */
  markIntroStepAsCompleted(stepLink) {
    if (!stepLink) return;
    
    const rewardId = stepLink.dataset.rewardId;
    if (rewardId) {
      const storageKey = `banzai_intro_completed_${rewardId}`;
      localStorage.setItem(storageKey, 'true');
      console.log(`Marked intro step for reward ${rewardId} as completed in localStorage`);
    }
  }
  
  /**
   * Navigate to the next step
   * Can be called using data-action="step-navigator#goToNextStep"
   */
  goToNextStep(event) {
    let currentStepLink = this.getCurrentStepLink();
    
    // If no current step found, try to find the intro step as fallback
    if (!currentStepLink) {
      const introStep = this.element.querySelector('.step-item .step-link[data-resource-type="intro"]');
      if (introStep) {
        currentStepLink = introStep;
        // Make sure the intro step is marked as current
        this.updateActiveStep(currentStepLink);
      }
    }
    
    if (!currentStepLink) {
      console.error('No current step link found');
      return;
    }
    
    // Get resource ID and reward ID for completion tracking
    const resourceId = currentStepLink.dataset.resourceId;
    const rewardId = currentStepLink.dataset.rewardId;
    const sponsorId = document.querySelector('meta[name="sponsor-id"]')?.content;
    
    // Check if this is an intro step (special handling)
    const isIntro = this.isIntroStep(currentStepLink);
    
    // Mark current step as UI completed first (for better user feedback)
    const currentStepItem = currentStepLink.closest('.step-item');
    if (currentStepItem) {
      currentStepItem.classList.add('step-completed');
      currentStepItem.classList.remove('step-current');
      
      // Update the checkmark - replace the container with SVG
      const stepIndicator = currentStepItem.querySelector('.step-indicator');
      if (stepIndicator) {
        stepIndicator.innerHTML = `
          <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 30C23.2875 30 30 23.2875 30 15C30 6.7125 23.2875 0 15 0C6.7125 0 0 6.7125 0 15C0 23.2875 6.7125 30 15 30ZM3 15C3 8.4 8.4 3 15 3C21.6 3 27 8.4 27 15C27 21.6 21.6 27 15 27C8.4 27 3 21.6 3 15ZM7.95 16.8C7.35 16.2375 7.35 15.2625 7.95 14.7C8.5125 14.1 9.4875 14.1 10.05 14.7L12.75 17.3625L19.95 10.2C20.5125 9.6 21.4875 9.6 22.05 10.2C22.65 10.7625 22.65 11.7375 22.05 12.3L13.8 20.55C13.5 20.85 13.125 21 12.75 21C12.375 21 12 20.85 11.7 20.55L7.95 16.8Z" fill="#00A53B"/>
          </svg>
        `;
      }
    }
    
    // Update progress after marking step as completed
    this.updateProgress();
    
    // For intro steps, use localStorage to mark completion instead of API call
    if (isIntro) {
      this.markIntroStepAsCompleted(currentStepLink);
      
      // Check if we should enable quiz and finish steps after intro completion
      this.updateQuizStepStatusIfNeeded();
      this.updateFinishStepStatusIfNeeded();
      
      // Navigate to the next uncompleted step
      this.navigateToNextUncompletedStep();
      return;
    }
    
    // Mark the resource as completed in the backend via API call for non-intro steps
    if (resourceId && rewardId) {
      console.log(`Marking resource ${resourceId} as completed for reward ${rewardId}`);
      
      // Prepare formData for the request
      const formData = new FormData();
      if (rewardId) formData.append('reward-id', rewardId);
      if (sponsorId) formData.append('sponsor-id', sponsorId);
      
      // Make the API call to mark resource as completed. /api4/ is required for this endpoint.
      fetch(`/api4/wellness/rewards/${resourceId}`, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin'
      })
      .then(response => {
        if (!response.ok) {
          console.error('Error marking resource as completed:', response.statusText);
        } else {
          console.log('Resource marked as completed successfully');
          
          // Check if all resources are now complete and update quiz step status if needed
          this.updateQuizStepStatusIfNeeded();
          
          // Check if we should enable the finish step
          this.updateFinishStepStatusIfNeeded();
        }
        
        // Navigate to the next uncompleted step after completion is recorded
        this.navigateToNextUncompletedStep();
      })
      .catch(error => {
        console.error('Error marking resource as completed:', error);
        // Still navigate to next uncompleted step even if completion tracking fails
        this.navigateToNextUncompletedStep();
      });
    } else {
      // If there's no resource ID or reward ID, just navigate to the next uncompleted step
      this.navigateToNextUncompletedStep();
    }
  }
  
  /**
   * Navigate to the next uncompleted step
   */
  navigateToNextUncompletedStep() {
    const nextUncompletedStep = this.findFirstUncompletedStep();
    
    if (nextUncompletedStep) {
      this.updateActiveStep(nextUncompletedStep);
      const url = nextUncompletedStep.dataset.stepUrl;
      const resourceType = nextUncompletedStep.dataset.resourceType;
      const stepTitle = nextUncompletedStep.closest('.step-item')?.querySelector('.step-title')?.textContent || '';
      
      // Show mobile content view if on mobile
      this.showMobileContentView(stepTitle);
      
      if (resourceType === 'intro') {
        this.loadContentForUrl(null);
      } else if (resourceType === 'finish') {
        this.showFinishStepContent();
      } else if (url) {
        this.loadContentForUrl(url);
      }
    } else {
      // All steps completed, navigate to finish step
      this.navigateToFinishStep();
    }
  }
  
  /**
   * Navigate back to the previous page
   * Can be called using data-action="step-navigator#goBack"
   */
  goBack(event) {
    event.preventDefault();
    const backUrl = event.currentTarget.dataset.backUrl;
    if (backUrl) {
      window.location.href = backUrl;
    } else {
      window.history.back();
    }
  }
  
  /**
   * Show loading indicator
   */
  showLoading() {
    // Add loading class to container
    this.element.classList.add('step-navigator-loading');
  }
  
  /**
   * Hide loading indicator
   */
  hideLoading() {
    // Remove loading class from container
    this.element.classList.remove('step-navigator-loading');
  }
  
  /**
   * Handle loading error
   */
  handleError() {
    this.element.classList.add('step-navigator-error');
    this.element.classList.remove('step-navigator-loading');
    
    // Dispatch error event
    this.element.dispatchEvent(new CustomEvent('step-navigator:error', {
      detail: { url: this.activeStepValue }
    }));
  }
  
  /**
   * Navigate to a specific step programmatically
   * Can be called using data-action="step-navigator#goToStep"
   */
  goToStep(event) {
    event.preventDefault();
    
    // Check if the step is disabled - don't navigate to disabled steps
    const isDisabled = event.currentTarget.hasAttribute('disabled') || 
                       event.currentTarget.closest('.step-disabled');
    
    if (isDisabled) {
      console.log('Cannot navigate to disabled step');
      return;
    }
    
    const stepUrl = event.currentTarget.dataset.stepUrl;
    const resourceType = event.currentTarget.dataset.resourceType;
    const stepTitle = event.currentTarget.closest('.step-item')?.querySelector('.step-title')?.textContent || '';
    
    // Update the active step first
    this.updateActiveStep(event.currentTarget);
    
    // Show mobile content view if on mobile
    this.showMobileContentView(stepTitle);
    
    // Handle different step types
    if (resourceType === 'intro') {
      // For intro steps, we don't need a URL, just load the intro content
      this.loadContentForUrl(null);
    } else if (resourceType === 'finish') {
      // For finish steps, show the finish content
      this.showFinishStepContent();
    } else if (stepUrl) {
      this.activeStepValue = stepUrl;
      this.loadContentForUrl(stepUrl);
    }
  }

  /**
   * Check if all resources are now complete and update quiz step status if needed
   */
  updateQuizStepStatusIfNeeded() {
    // Find all resource steps (non-intro, non-quiz, non-finish steps)
    const allSteps = this.element.querySelectorAll('.step-item');
    const resourceSteps = Array.from(allSteps).filter(step => {
      const stepLink = step.querySelector('.step-link');
      const resourceType = stepLink?.dataset.resourceType;
      return resourceType && 
             resourceType !== 'intro' && 
             resourceType !== 'quiz' && 
             resourceType !== 'finish';
    });
    
    // Check if all resource steps are completed
    const completedResourceSteps = resourceSteps.filter(step => 
      step.classList.contains('step-completed')
    );
    
    const allResourcesComplete = resourceSteps.length > 0 && 
                                completedResourceSteps.length === resourceSteps.length;
    
    if (allResourcesComplete) {
      // Find the quiz step and update its status
      const quizStep = this.element.querySelector('.step-link[data-resource-type="quiz"]');
      const quizStepItem = quizStep?.closest('.step-item');
      
      if (quizStepItem && quizStepItem.classList.contains('step-disabled')) {
        // Update quiz step from disabled to pending
        quizStepItem.classList.remove('step-disabled');
        quizStepItem.classList.add('step-pending');
        
        // Remove disabled attribute from the link
        if (quizStep) {
          quizStep.removeAttribute('disabled');
        }
        
        console.log('Quiz step enabled - all resources are now complete');
      }
    }
  }

  /**
   * Check if we should enable the finish step
   */
  updateFinishStepStatusIfNeeded() {
    // Find all non-finish steps (intro, resources, quiz)
    const allSteps = this.element.querySelectorAll('.step-item');
    const nonFinishSteps = Array.from(allSteps).filter(step => {
      const stepLink = step.querySelector('.step-link');
      const resourceType = stepLink?.dataset.resourceType;
      return resourceType && resourceType !== 'finish';
    });
    
    // Check if all non-finish steps are completed
    const completedNonFinishSteps = nonFinishSteps.filter(step => 
      step.classList.contains('step-completed')
    );
    
    const allNonFinishStepsComplete = nonFinishSteps.length > 0 && 
                                     completedNonFinishSteps.length === nonFinishSteps.length;
    
    if (allNonFinishStepsComplete) {
      // Find the finish step and update its status
      const finishStep = this.element.querySelector('.step-link[data-resource-type="finish"]');
      const finishStepItem = finishStep?.closest('.step-item');
      
      if (finishStepItem && finishStepItem.classList.contains('step-disabled')) {
        // Update finish step from disabled to pending
        finishStepItem.classList.remove('step-disabled');
        finishStepItem.classList.add('step-pending');
        
        // Remove disabled attribute from the link
        if (finishStep) {
          finishStep.removeAttribute('disabled');
        }
        
        console.log('Finish step enabled - all required steps are now complete');
             }
     }
   }

   /**
    * Mark all steps as completed (visual only - no REST calls)
    * Global function accessible as window.BanzaiStepNavigator_completeAll()
    */
   markAllStepsCompleted() {
     console.log('Marking all steps as completed (visual only)');
     
     // Get all step items
     const allStepItems = this.element.querySelectorAll('.step-item');
     
     allStepItems.forEach(stepItem => {
       // Remove any existing status classes
       stepItem.classList.remove('step-current', 'step-pending', 'step-disabled');
       
       // Add completed class
       stepItem.classList.add('step-completed');
       
       // Update the step indicator to show checkmark
       const stepIndicator = stepItem.querySelector('.step-indicator');
       if (stepIndicator) {
         stepIndicator.innerHTML = `
           <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
             <path d="M15 30C23.2875 30 30 23.2875 30 15C30 6.7125 23.2875 0 15 0C6.7125 0 0 6.7125 0 15C0 23.2875 6.7125 30 15 30ZM3 15C3 8.4 8.4 3 15 3C21.6 3 27 8.4 27 15C27 21.6 21.6 27 15 27C8.4 27 3 21.6 3 15ZM7.95 16.8C7.35 16.2375 7.35 15.2625 7.95 14.7C8.5125 14.1 9.4875 14.1 10.05 14.7L12.75 17.3625L19.95 10.2C20.5125 9.6 21.4875 9.6 22.05 10.2C22.65 10.7625 22.65 11.7375 22.05 12.3L13.8 20.55C13.5 20.85 13.125 21 12.75 21C12.375 21 12 20.85 11.7 20.55L7.95 16.8Z" fill="#00A53B"/>
           </svg>
         `;
       }
       
       // Enable the step link (remove disabled attribute)
       const stepLink = stepItem.querySelector('.step-link');
       if (stepLink) {
         stepLink.removeAttribute('disabled');
       }
     });
     
     // Mark intro steps as completed in localStorage for persistence
     const introSteps = Array.from(allStepItems).filter(step => 
       step.querySelector('.step-link')?.dataset.resourceType === 'intro'
     );
     
     introSteps.forEach(introStep => {
       const stepLink = introStep.querySelector('.step-link');
       const rewardId = stepLink?.dataset.rewardId;
       if (rewardId) {
         const storageKey = `banzai_intro_completed_${rewardId}`;
         localStorage.setItem(storageKey, 'true');
       }
     });
     
     // Update progress to 100%
     this.updateProgress();
     
     console.log('All steps marked as completed');
   }
 }